import { REGISTER_STATUS_SORT_VALUES } from './constants/constants';

// Fields whose missing value sorts last in both directions, as on the project and team views:
// a company with no known register status is not "lower" than an active one, it is unknown.
const NULLS_LAST_SORT_KEYS = new Set(REGISTER_STATUS_SORT_VALUES.map((v) => v.split('-')[0]));

/**
 * Creates a numeric comparator for sorting arrays.
 * Null/undefined values are treated as smallest (-Infinity), except for the keys above, where
 * they always come last.
 * @param {string} key - The property key to sort by
 * @param {'asc'|'desc'} direction - Sort direction
 * @returns {Function} Comparator function for Array.sort()
 */
const createNumericComparator = (key, direction) => {
  const missing = NULLS_LAST_SORT_KEYS.has(key) && direction === 'asc' ? Infinity : -Infinity;
  if (direction === 'asc') {
    return (a, b) => +(a[key] ?? missing) - +(b[key] ?? missing);
  }
  return (a, b) => +(b[key] ?? missing) - +(a[key] ?? missing);
};

/**
 * Apply the Profiler list's selected sort to an array (mutates the input).
 * Shared by `state.sortedResults` (full non-excluded set) and
 * `state.filteredAndSortedResults` (the V1 Spotlight+column-filtered set),
 * so both stay in lock-step if the sort options ever evolve.
 *
 * @param {Array}  results       Array of company entries (mutated in place).
 * @param {string} selectedSort  Sort key from `state.selectedSort`.
 * @returns {Array} The same array, sorted.
 */
const applySelectedSort = (results, selectedSort) => {
  if (selectedSort === 'name-asc') {
    results.sort((a, b) =>
      (a.name || a.companyName || '').localeCompare(b.name || b.companyName || '', 'en', {
        sensitivity: 'base'
      })
    );
  } else if (selectedSort === 'name-desc') {
    results.sort((a, b) =>
      (b.name || b.companyName || '').localeCompare(a.name || a.companyName || '', 'en', {
        sensitivity: 'base'
      })
    );
  } else if (selectedSort !== 'default') {
    // Sort by numeric field (e.g., revenue, employees).
    // Null values are treated as smallest (-Infinity):
    // - Ascending: nulls appear first (smallest to largest)
    // - Descending: nulls appear last (largest to smallest)
    // Register status keeps unknown last in both (see NULLS_LAST_SORT_KEYS).
    const [propKey, typeSort] = selectedSort.split('-');
    results.sort(createNumericComparator(propKey, typeSort));
  }
  return results;
};

export default applySelectedSort;
